/* eslint-disable max-len */
import {
    MutableRefObject,
    useCallback,
    useMemo,
    useState,
} from 'react'
import {
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
    useQueryClient,
} from '@tanstack/react-query'
import Box from '@mui/material/Box'

import {
    RequestKeys,
} from 'app/hooks/useRequest'
import SkyNetMasterDataGrid, {
    useDataGridSource,
} from 'app/shared-components/SkyNetMasterDataGrid'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    SkyDataGridColumnDefinition,
    ExtendedGridApiPro,
    DataGridSupportedFeatures,
    featuresToHiddenColumns,
} from 'app/shared-components/SkyBaseDataGrid'
import {
    SkyNetTableTab,
} from 'app/shared-components/SkyNetTable/types/skyNetTable.types'
import {
    SkyNetObjectConfigParams,
    SkyNetObjectTabsOutletContext,
    SkyNetMasterTabConfig,
} from './SkyNetObjectRoutes.types'
import useStyles from './SkyNetObjectProfile.styles'
import {
    prepareMasterConfig,
} from './SkyNetObjectProfile.utils'
import useDetailedViewOutlet from './hooks/useDetailedViewOutlet'
import Tabs from './Tabs'
import useObjectProfileRowSelectionModel from './hooks/useObjectProfileRowSelectionModel'
import useAllowedActionKeys from './hooks/useAllowedActionKeys'
import {
    SkyNetObjectProfileContext,
} from './SkyNetObjectProfileContext'

const toolbarControls = []
const defaultFeaturesLists: DataGridSupportedFeatures[] = []
const defaultFiltersUnrelatedToColumns = []

type SkyNetObjectProfileProps = {
    filtersUnrelatedToColumns?: SkyDataGridColumnDefinition[],
    config: SkyNetObjectConfigParams<any>,
    featuresLists?: DataGridSupportedFeatures[],
}

export default function SkyNetObjectProfile({
    filtersUnrelatedToColumns = defaultFiltersUnrelatedToColumns,
    config,
    featuresLists = defaultFeaturesLists,
}: Readonly<SkyNetObjectProfileProps>) {
    const {
        classes,
    } = useStyles()
    const {
        id,
        isDetailsView,
        locationPathname,
        navigate,
        tablePath,
        Outlet,
        activeTabName,
    } = useDetailedViewOutlet()
    const gridApiRef: MutableRefObject<ExtendedGridApiPro> = useGridApiRef<ExtendedGridApiPro>()
    const queryClient = useQueryClient()
    const uniqField = useMemo(() => {
        return config.uniqField || 'id'
    }, [config])

    const additionalColumnsForFilters: SkyDataGridColumnDefinition<any>[] = useMemo(() => {
        const hiddenColumns = featuresToHiddenColumns(featuresLists)

        return [
            ...filtersUnrelatedToColumns,
            ...hiddenColumns,
        ]
    }, [
        filtersUnrelatedToColumns,
        featuresLists,
    ])

    const tableConfig = useMemo(() => {
        return prepareMasterConfig(config)
    }, [config])

    const tableTabsConfig = useMemo(() => {
        return tableConfig.map(({
            name, url,
        }) => {
            return {
                name,
                url,
            } as SkyNetTableTab
        })
    }, [tableConfig])

    const activeTab = useMemo(() => {
        return tableTabsConfig.findIndex((tab) => {
            return tab.url === activeTabName
        })
    }, [
        activeTabName,
        tableTabsConfig,
    ])

    const {
        columns,
        request,
        requestKey,
        actionComponents: tabActionComponents,
    } = useMemo(() => {
        return (tableConfig[activeTab] || {}) as SkyNetMasterTabConfig<any>
    }, [
        activeTab,
        tableConfig,
    ])
    const actionComponents = useMemo(() => {
        return tabActionComponents || config.actionComponents
    }, [
        config.actionComponents,
        tabActionComponents,
    ])

    const allowedActionKeys = useAllowedActionKeys({
        actionComponents,
    })

    const customDataSource = useDataGridSource({
        request,
        key: requestKey,
    })

    const onActiveTabChange = useCallback((newActiveTab: number) => {
        navigate(`${tablePath}/${tableTabsConfig[newActiveTab].url}`)
        customDataSource.invalidate()
    }, [
        customDataSource,
        navigate,
        tablePath,
        tableTabsConfig,
    ])

    /* To refresh data grid from outside */
    const refreshDataGrid = useCallback((refreshRequestKey: RequestKeys) => {
        /* invalidate queries for dataGrid */
        queryClient.invalidateQueries({
            queryKey: [refreshRequestKey],
        })
        gridApiRef.current?.dataSource.fetchRows()
    }, [
        gridApiRef,
        queryClient,
    ])

    const {
        rowSelectionModel, onRowSelectionModelChange,
    } = useObjectProfileRowSelectionModel(id)

    const currentDetailsTabUrl = useMemo(() => {
        const match = locationPathname.match(
            new RegExp(`${tablePath}/${activeTabName}/${CommonRoutes.Edit}/${id}/([^/]+)`),
        )

        return match?.[1] ? `/${match[1]}` : ''
    }, [
        activeTabName,
        id,
        locationPathname,
        tablePath,
    ])

    const onRowClick = useCallback(({
        row,
    }) => {
        const newId = row[uniqField]

        if (String(newId) === String(id)) {
            navigate('..')
            return
        }
        if (isDetailsView) {
            navigate(`./${CommonRoutes.Edit}/${newId}${currentDetailsTabUrl}`)
        } else {
            navigate(`./${CommonRoutes.Edit}/${newId}`)
        }
    }, [
        id,
        navigate,
        uniqField,
        isDetailsView,
        currentDetailsTabUrl,
    ])

    /* passing custom props to components rendered in the Outlet */
    const outletContext: SkyNetObjectTabsOutletContext = useMemo(() => {
        return {
            refreshTable: refreshDataGrid,
        }
    }, [refreshDataGrid])

    const tabsComponent = useMemo(() => {
        return (
            <Tabs
                tabs={tableTabsConfig}
                isDropDownView={isDetailsView}
                activeTab={activeTab}
                onChange={onActiveTabChange}
            />
        )
    }, [
        activeTab,
        isDetailsView,
        onActiveTabChange,
        tableTabsConfig,
    ])

    const [
        page,
        setPage,
    ] = useState(0)

    const skyNetObjectProfileContextValue = useMemo(() => {
        return {
            page,
            setPage,
        }
    }, [page])

    return (
        <Box className={classes.container}>
            <SkyNetObjectProfileContext.Provider value={skyNetObjectProfileContextValue}>
                <SkyNetMasterDataGrid
                    gridApiRef={gridApiRef}
                    persistentKey={config.persistentKey}
                    columns={columns as unknown as SkyDataGridColumnDefinition<any>[]}
                    filtersUnrelatedToColumns={additionalColumnsForFilters}
                    customDataSource={customDataSource}
                    toolbarControls={toolbarControls}
                    tabsComponent={tabsComponent}
                    onRowClick={onRowClick}
                    isDetailsView={isDetailsView}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    toolbarActions={allowedActionKeys}
                />
                <Outlet
                    key={locationPathname}
                    context={outletContext}
                />
            </SkyNetObjectProfileContext.Provider>
        </Box>
    )
}
